import {
	Card,
	CardContent,
	CardHeader,
	CardTitle,
	CardDescription,
} from '@/components/ui/card'
import { CodeBlock } from '../CodeBlock'
import { SectionHeader } from '../SectionHeader'
import { useTranslation } from '@/hooks/useTranslation'

export const LanguageConfigurationSection = () => {
	const { t } = useTranslation()
	
	return (
		<section id="language-configuration" className="mb-16">
			<SectionHeader id="language-configuration">
				{t('docs.languageConfiguration.title')}
			</SectionHeader>

			<Card className="mb-8 shadow-sm dark:bg-gray-800/50">
				<CardHeader>
					<CardTitle className="text-xl font-semibold">
						{t('docs.languageConfiguration.availableLanguages.title')}
					</CardTitle>
					<CardDescription className="dark:text-gray-400">
						{t('docs.languageConfiguration.availableLanguages.description')}
					</CardDescription>
				</CardHeader>
				<CardContent className="text-gray-700 dark:text-gray-300 space-y-4">
					<p>
						{t('docs.languageConfiguration.configureHint.prefix')}{' '}
						<code className="bg-gray-200 dark:bg-gray-700 px-1 py-0.5 rounded text-xs font-semibold">
							data-language
						</code>{' '}
						{t('docs.languageConfiguration.configureHint.suffix')}
					</p>

				<div className="grid md:grid-cols-2 gap-4">
					<div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-700">
						<h4 className="font-semibold text-blue-800 dark:text-blue-200 mb-2">
							{t('docs.languageConfiguration.languages.english')}
						</h4>
						<code className="text-sm bg-blue-100 dark:bg-blue-800 px-2 py-1 rounded font-mono">
							data-language="en"
						</code>
					</div>
					<div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-700">
						<h4 className="font-semibold text-green-800 dark:text-green-200 mb-2">
							{t('docs.languageConfiguration.languages.french')}
						</h4>
						<code className="text-sm bg-green-100 dark:bg-green-800 px-2 py-1 rounded font-mono">
							data-language="fr"
						</code>
					</div>
				</div>

				<div className="grid md:grid-cols-2 gap-4 mt-4">
					<div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-200 dark:border-yellow-700">
						<h4 className="font-semibold text-yellow-800 dark:text-yellow-200 mb-2">
							{t('docs.languageConfiguration.languages.german')}
						</h4>
						<code className="text-sm bg-yellow-100 dark:bg-yellow-800 px-2 py-1 rounded font-mono">
							data-language="de"
						</code>
					</div>
					<div className="p-4 bg-orange-50 dark:bg-orange-900/20 rounded-lg border border-orange-200 dark:border-orange-700">
						<h4 className="font-semibold text-orange-800 dark:text-orange-200 mb-2">
							{t('docs.languageConfiguration.languages.spanish')}
						</h4>
						<code className="text-sm bg-orange-100 dark:bg-orange-800 px-2 py-1 rounded font-mono">
							data-language="es"
						</code>
					</div>
				</div>

				<div className="grid md:grid-cols-2 gap-4 mt-4">
					<div className="p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg border border-purple-200 dark:border-purple-700">
						<h4 className="font-semibold text-purple-800 dark:text-purple-200 mb-2">
							{t('docs.languageConfiguration.languages.chinese')}
						</h4>
						<code className="text-sm bg-purple-100 dark:bg-purple-800 px-2 py-1 rounded font-mono">
							data-language="zh"
						</code>
					</div>
					<div className="p-4 bg-pink-50 dark:bg-pink-900/20 rounded-lg border border-pink-200 dark:border-pink-700">
						<h4 className="font-semibold text-pink-800 dark:text-pink-200 mb-2">
							{t('docs.languageConfiguration.languages.khmer')}
						</h4>
						<code className="text-sm bg-pink-100 dark:bg-pink-800 px-2 py-1 rounded font-mono">
							data-language="km"
						</code>
					</div>
				</div>

				<div className="grid md:grid-cols-1 gap-4 mt-4">
					<div className="p-4 bg-lime-50 dark:bg-lime-900/20 rounded-lg border border-lime-200 dark:border-lime-700">
						<h4 className="font-semibold text-lime-800 dark:text-lime-200 mb-2">
							{t('docs.languageConfiguration.languages.vietnamese')}
						</h4>
						<code className="text-sm bg-lime-100 dark:bg-lime-800 px-2 py-1 rounded font-mono">
							data-language="vi"
						</code>
					</div>
				</div>

				<div className="mt-6">
					<h4 className="font-semibold mb-3">
						{t('docs.languageConfiguration.example.title')}
					</h4>
					<CodeBlock
						code={`<script
    defer
    src="https://cdn.feedbask.com/widget.js"
    data-client-key="YOUR_WIDGET_ID"
    data-language="fr"
    id="feedbask-widget-script"
></script>`}
						language="html"
						copyLabel={t('docs.languageConfiguration.example.copyLabel')}
						copyMessage={t('docs.languageConfiguration.example.copyMessage')}
					/>
				</div>

				<div className="mt-4 p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-md border border-yellow-200 dark:border-yellow-700">
					<p className="text-sm text-yellow-800 dark:text-yellow-200">
						<strong>{t('docs.languageConfiguration.note.label')}</strong>{' '}
						{t('docs.languageConfiguration.note.text')}
					</p>
				</div>
			</CardContent>
		</Card>
	</section>
	)
}